import axiosInstance from '../utils/axios';
import API_CONFIG from '../config/api.config';

const customPrintTemplateService = {
  /**
   * Create a custom template from a base format
   * @param {Object} request - { templateName, baseFormat }
   * @returns {Promise} Template response
   */
  createFromBaseFormat: async (request) => {
    try {
      const response = await axiosInstance.post(
        `${API_CONFIG.ENDPOINTS.CUSTOM_PRINT_TEMPLATES}/create-from-base`,
        request
      );
      return response.data.template || response.data;
    } catch (error) {
      console.error('Error creating template from base format:', error);
      throw error;
    }
  },

  /**
   * Create a new custom template
   * @param {Object} template - Template data
   * @returns {Promise} Template response
   */
  createTemplate: async (template) => {
    try {
      const response = await axiosInstance.post(
        API_CONFIG.ENDPOINTS.CUSTOM_PRINT_TEMPLATES,
        template
      );
      return response.data.template || response.data;
    } catch (error) {
      console.error('Error creating template:', error);
      // If format_type constraint violation, try to get existing template
      if (error.response?.status === 500 && 
          error.response?.data?.message?.includes('uk_template_format_type')) {
        // Try to find existing template with same format_type
        const allTemplates = await customPrintTemplateService.getAllTemplates();
        const existing = allTemplates.find(t => t.formatType === template.formatType);
        if (existing) {
          return existing;
        }
      }
      throw error;
    }
  },

  /**
   * Update an existing custom template
   * @param {number} templateId - Template ID
   * @param {Object} template - Updated template data
   * @returns {Promise} Template response
   */
  updateTemplate: async (templateId, template) => {
    try {
      const response = await axiosInstance.put(
        `${API_CONFIG.ENDPOINTS.CUSTOM_PRINT_TEMPLATES}/${templateId}`,
        template
      );
      return response.data.template || response.data;
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  },

  /**
   * Get template by ID
   * @param {number} templateId - Template ID
   * @returns {Promise} Template response
   */
  getTemplateById: async (templateId) => {
    try {
      const response = await axiosInstance.get(
        `${API_CONFIG.ENDPOINTS.CUSTOM_PRINT_TEMPLATES}/${templateId}`
      );
      return response.data.template || response.data;
    } catch (error) {
      console.error('Error fetching template:', error);
      throw error;
    }
  },

  /**
   * Get all templates for current company
   * @returns {Promise} List of templates
   */
  getAllTemplates: async () => {
    try {
      const response = await axiosInstance.get(
        API_CONFIG.ENDPOINTS.CUSTOM_PRINT_TEMPLATES
      );
      return response.data.templates || response.data || [];
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  },

  /**
   * Get templates by base format
   * @param {string} baseFormat - Base format (A4, 3_INCH_SIMPLE, 3_INCH_GST_DETAILS)
   * @returns {Promise} List of templates
   */
  getTemplatesByBaseFormat: async (baseFormat) => {
    try {
      const response = await axiosInstance.get(
        `${API_CONFIG.ENDPOINTS.CUSTOM_PRINT_TEMPLATES}/base-format/${baseFormat}`
      );
      return response.data.templates || response.data || [];
    } catch (error) {
      console.error('Error fetching templates by base format:', error);
      throw error;
    }
  },

  /**
   * Get active template for current company
   * @returns {Promise} Active template or null
   */
  getActiveTemplate: async () => {
    try {
      const response = await axiosInstance.get(
        `${API_CONFIG.ENDPOINTS.CUSTOM_PRINT_TEMPLATES}/active`
      );
      return response.data.template || response.data || null;
    } catch (error) {
      console.error('Error fetching active template:', error);
      throw error;
    }
  },

  /**
   * Activate a template
   * @param {number} templateId - Template ID
   * @returns {Promise} Template response
   */
  activateTemplate: async (templateId) => {
    try {
      const response = await axiosInstance.post(
        `${API_CONFIG.ENDPOINTS.CUSTOM_PRINT_TEMPLATES}/${templateId}/activate`
      );
      return response.data.template || response.data;
    } catch (error) {
      console.error('Error activating template:', error);
      throw error;
    }
  },

  /**
   * Delete a template
   * @param {number} templateId - Template ID
   * @returns {Promise} Delete response
   */
  deleteTemplate: async (templateId) => {
    try {
      const response = await axiosInstance.delete(
        `${API_CONFIG.ENDPOINTS.CUSTOM_PRINT_TEMPLATES}/${templateId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  },

  /**
   * Get default format structure for customization
   * @param {string} baseFormat - Base format (A4, 3_INCH_SIMPLE, 3_INCH_GST_DETAILS)
   * @returns {Promise} Default structure
   */
  getDefaultFormatStructure: async (baseFormat) => {
    try {
      const response = await axiosInstance.get(
        `${API_CONFIG.ENDPOINTS.CUSTOM_PRINT_TEMPLATES}/default-structure/${baseFormat}`
      );
      return response.data.structure || response.data;
    } catch (error) {
      console.error('Error fetching default format structure:', error);
      throw error;
    }
  },

  /**
   * Preview custom template
   * @param {number} templateId - Template ID
   * @returns {Promise<Blob>} PDF document as a Blob
   */
  previewTemplate: async (templateId) => {
    try {
      const response = await axiosInstance.get(
        `${API_CONFIG.ENDPOINTS.CUSTOM_PRINT_TEMPLATES}/${templateId}/preview`,
        {
          responseType: 'blob'
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error generating preview:', error);
      throw error;
    }
  }
};

export default customPrintTemplateService;

